export type ScreenGuideSlide = {
  id: string
  title: string
  description: string
  image: string
  alt?: string
}

export type ScreenGuideConfig = {
  id: string
  title: string
  eyebrow?: string
  description?: string
  slides: ScreenGuideSlide[]
}

type RawGuideModule = {
  default?: unknown
}

const guideModules = import.meta.glob<RawGuideModule>('../../content/screen-guides/**/guide.json', {
  eager: true,
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const isSlide = (value: unknown): value is ScreenGuideSlide => {
  if (!isRecord(value)) return false
  return (
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.description === 'string' &&
    typeof value.image === 'string' &&
    (typeof value.alt === 'undefined' || typeof value.alt === 'string')
  )
}

const parseGuideConfig = (value: unknown, sourcePath: string): ScreenGuideConfig | null => {
  if (!isRecord(value)) return null
  if (typeof value.id !== 'string' || typeof value.title !== 'string') return null
  if (!Array.isArray(value.slides) || value.slides.length === 0 || !value.slides.every(isSlide)) return null
  if (typeof value.eyebrow !== 'undefined' && typeof value.eyebrow !== 'string') return null
  if (typeof value.description !== 'undefined' && typeof value.description !== 'string') return null

  const folderMatch = sourcePath.match(/screen-guides\/(.+)\/guide\.json$/)
  const expectedId = folderMatch?.[1]?.replace(/\//g, '.')
  const id = value.id

  return {
    id: expectedId && id !== expectedId ? expectedId : id,
    title: value.title,
    eyebrow: value.eyebrow,
    description: value.description,
    slides: value.slides,
  }
}

const guideEntries = Object.entries(guideModules)
  .map(([path, module]) => parseGuideConfig(module.default ?? module, path))
  .filter((config): config is ScreenGuideConfig => Boolean(config))

const guideMap = new Map(guideEntries.map((config) => [config.id, config]))

const fallbackGuide: ScreenGuideConfig = {
  id: 'fallback',
  eyebrow: 'Quick tour',
  title: 'Screen guide',
  description: 'Replace this dummy guide with screenshots for this screen when the page content is ready.',
  slides: [
    {
      id: 'overview',
      title: 'See what matters first',
      description: 'Use this first slide for the screen overview and the main jobs people can do here.',
      image: '/screen-guides/shared/dummy-overview.svg',
      alt: 'Dummy overview screenshot',
    },
    {
      id: 'actions',
      title: 'Try the core actions',
      description: 'Use this slide for create, edit, assign, review, or sync actions on the screen.',
      image: '/screen-guides/shared/dummy-actions.svg',
      alt: 'Dummy action screenshot',
    },
    {
      id: 'details',
      title: 'Review details and next steps',
      description: 'Use this slide for filters, sidecars, status controls, or detail panels.',
      image: '/screen-guides/shared/dummy-details.svg',
      alt: 'Dummy detail screenshot',
    },
  ],
}

export const getScreenGuideConfig = (screenId: string | null | undefined): ScreenGuideConfig | null => {
  if (!screenId) return null
  return guideMap.get(screenId) ?? {
    ...fallbackGuide,
    id: screenId,
    title: screenId
      .split('.')
      .slice(-1)[0]
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase()),
  }
}

export const hasCustomScreenGuideConfig = (screenId: string): boolean => guideMap.has(screenId)
